import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  UserIcon,
  LockClosedIcon,
  ExclamationCircleIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';

/* ─── Composant motif géométrique gauche ─── */
const GeometricPattern: React.FC = () => (
  <svg
    className="absolute inset-0 w-full h-full opacity-10"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <pattern id="grid" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
        <path d="M 48 0 L 0 0 0 48" fill="none" stroke="white" strokeWidth="0.8" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#grid)" />
    <circle cx="20%" cy="20%" r="120" fill="white" fillOpacity="0.04" />
    <circle cx="80%" cy="75%" r="200" fill="white" fillOpacity="0.04" />
    <circle cx="50%" cy="50%" r="80" fill="white" fillOpacity="0.03" />
  </svg>
);

/* ─── Spinner SVG réutilisable ─── */
const Spinner: React.FC = () => (
  <svg
    className="animate-spin h-5 w-5 text-white"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/* ─── Panneau gauche branding partagé ─── */
export const BrandPanel: React.FC<{ subtitle?: string }> = ({ subtitle }) => (
  <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 relative bg-gradient-to-br from-primary-700 via-primary-600 to-green-500 flex-col items-center justify-center p-12 overflow-hidden">
    <GeometricPattern />

    {/* Badge gouvernemental */}
    <div className="relative z-10 mb-10 flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 backdrop-blur-sm">
      <span className="w-2 h-2 rounded-full bg-yellow-300 animate-pulse" />
      <span className="text-white/90 text-xs font-medium tracking-widest uppercase">
        Burkina Faso — Gouvernement
      </span>
    </div>

    {/* Logo principal */}
    <div className="relative z-10 flex flex-col items-center text-center">
      <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-2xl overflow-hidden">
        <img src="/armoirie.jpeg" alt="Armoiries du Burkina Faso" className="w-full h-full object-contain p-1" />
      </div>

      <h1 className="text-4xl font-extrabold text-white leading-tight tracking-tight">
        Suivi Démat
      </h1>
      <p className="mt-1 text-lg font-semibold text-green-100 tracking-wide">MTDPCE</p>

      <div className="mt-4 w-16 h-0.5 bg-white/30 rounded-full" />

      <p className="mt-4 text-sm text-green-100/80 max-w-xs leading-relaxed">
        {subtitle ||
          'Plateforme nationale de suivi de la dématérialisation des procédures administratives'}
      </p>
    </div>

    {/* Éléments décoratifs géométriques */}
    <div className="relative z-10 mt-16 grid grid-cols-3 gap-3 opacity-40">
      {[...Array(9)].map((_, i) => (
        <div
          key={i}
          className="w-8 h-8 border border-white/50 rounded-md"
          style={{ animationDelay: `${i * 100}ms` }}
        />
      ))}
    </div>

    {/* Footer panneau gauche */}
    <div className="absolute bottom-6 left-0 right-0 flex justify-center z-10">
      <p className="text-white/40 text-xs">© 2026 MTDPCE — Tous droits réservés</p>
    </div>
  </div>
);

/* ══════════════════════════════════════
   PAGE LOGIN
══════════════════════════════════════ */
const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorType, setErrorType] = useState<'credentials' | 'unverified' | 'pending' | 'disabled'>('credentials');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResendSent(false);
    setIsLoading(true);
    try {
      await login({ email, password });
      navigate('/');
    } catch (err: any) {
      const msg: string = err.response?.data?.message || '';
      const status: number = err.response?.status;
      if (status === 400 && msg === 'email_non_verifie') {
        setErrorType('unverified');
        setError('email_non_verifie');
      } else if (status === 400 && msg.includes('attente')) {
        setErrorType('pending');
        setError(msg);
      } else if (status === 400 && msg.includes('désactivé')) {
        setErrorType('disabled');
        setError(msg);
      } else {
        setErrorType('credentials');
        setError(msg || 'Email ou mot de passe incorrect');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await import('../services/api').then(m => m.default.resendVerification(email));
      setResendSent(true);
    } catch { /* silencieux */ }
    finally { setResendLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Panneau gauche (branding) ── */}
      <BrandPanel />

      {/* ── Panneau droit (formulaire) ── */}
      <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 py-12 sm:px-12">

        {/* Logo mobile uniquement */}
        <div className="lg:hidden flex flex-col items-center mb-10">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-3 shadow-lg overflow-hidden border border-gray-100">
            <img src="/armoirie.jpeg" alt="Armoiries du Burkina Faso" className="w-full h-full object-contain p-1" />
          </div>
          <span className="text-xl font-bold text-gray-900">Suivi Démat</span>
          <span className="text-xs text-primary-600 font-semibold tracking-widest uppercase mt-0.5">MTDPCE</span>
        </div>

        {/* Carte formulaire */}
        <div className="w-full max-w-md">

          {/* En-tête */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 leading-tight">
              Connexion à votre espace
            </h2>
            <p className="mt-1.5 text-sm text-gray-500">
              Entrez vos identifiants pour accéder à la plateforme
            </p>
          </div>

          {/* ── Mauvais identifiants ── */}
          {error && errorType === 'credentials' && (
            <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3.5 rounded-xl text-sm animate-[fadeIn_0.25s_ease-in-out]" role="alert">
              <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0 text-red-500 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* ── Email non vérifié ── */}
          {errorType === 'unverified' && (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl px-4 py-4 animate-[fadeIn_0.25s_ease-in-out]" role="alert">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-blue-800 mb-0.5">Email non vérifié</p>
                  <p className="text-sm text-blue-700 leading-relaxed mb-3">
                    Veuillez vérifier votre adresse email avant de vous connecter. Consultez votre boîte de réception.
                  </p>
                  {resendSent ? (
                    <p className="text-xs font-medium text-blue-600">
                      Un nouveau lien a été envoyé à <span className="font-semibold">{email}</span>.
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resendLoading}
                      className="text-xs font-semibold text-blue-700 underline hover:text-blue-900 disabled:opacity-50 transition-colors"
                    >
                      {resendLoading ? 'Envoi en cours…' : 'Renvoyer le lien de vérification'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── En attente / désactivé ── */}
          {error && (errorType === 'pending' || errorType === 'disabled') && (
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 animate-[fadeIn_0.25s_ease-in-out]" role="alert">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-0.5">
                    {errorType === 'pending' ? "Compte en attente d'activation" : 'Compte désactivé'}
                  </p>
                  <p className="text-sm text-amber-700 leading-relaxed">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit} noValidate>

            {/* Champ email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Adresse email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white transition-all duration-150 text-sm"
                  placeholder="exemple@demat.gov.bf"
                />
              </div>
            </div>

            {/* Champ mot de passe */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Mot de passe
                </label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <LockClosedIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full pl-10 pr-11 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent focus:bg-white transition-all duration-150 text-sm"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Bouton connexion */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 rounded-xl text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150 shadow-sm shadow-primary-200 mt-2"
            >
              {isLoading ? (
                <>
                  <Spinner />
                  <span>Connexion en cours…</span>
                </>
              ) : (
                <>
                  <span>Se connecter</span>
                  <ArrowRightIcon className="h-4 w-4" />
                </>
              )}
            </button>
          </form>

          {/* Séparateur */}
          <div className="mt-8 pt-6 border-t border-gray-100 space-y-3">
            <p className="text-center text-sm text-gray-500">
              Pas encore de compte ?{' '}
              <Link to="/register" className="font-semibold text-primary-600 hover:text-primary-700 hover:underline transition-colors">
                Demander un accès Responsable
              </Link>
            </p>
            <p className="text-center text-xs text-gray-400">
              Accès réservé aux agents accrédités du MTDPCE
            </p>
          </div>
        </div>

        {/* Footer mobile */}
        <p className="lg:hidden mt-8 text-center text-xs text-gray-400">
          © 2026 MTDPCE — Burkina Faso
        </p>
      </div>
    </div>
  );
};

export default Login;
